import {
  type FC,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import css from './styles.module.scss';

interface ScratchSurfaceProps {
  /** Stable id of the underlying ticket — remounts surface when it changes. */
  ticketId: string;
  /** Disabled state — used while the reveal animation is running or after claim. */
  disabled?: boolean;
  /** Coverage threshold (0..1) at which we auto-clear the rest. Default 0.55. */
  threshold?: number;
  /** Brush radius in CSS pixels. */
  radius?: number;
  onProgress?: (progress: number) => void;
  onReveal?: () => void;
  className?: string;
}

const SAMPLE_STEP = 24; // px between coverage samples

/**
 * Canvas-based scratch foil. Uses pointer events so it works for both touch
 * (Telegram mobile) and desktop. The foil is an opaque holographic gradient
 * painted on a canvas; we erase it with `destination-out` along the pointer
 * path. Coverage is sampled on a sparse grid for performance — once the
 * sampled coverage crosses the threshold we trigger an animated wipe of the
 * remainder so the user never has to scratch every pixel.
 */
const ScratchSurface: FC<ScratchSurfaceProps> = ({
  ticketId,
  disabled,
  threshold = 0.55,
  radius = 26,
  onProgress,
  onReveal,
  className,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const revealedRef = useRef(false);
  const sizeRef = useRef({ w: 0, h: 0 });
  const dprRef = useRef(1);

  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(true);

  const paintFoil = useCallback((): void => {
    const canvas = canvasRef.current;

    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;
    const { w, h } = sizeRef.current;

    if (w === 0 || h === 0) return;

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);

    // Silver scratch foil — neutral cool gray with subtle vertical sheen,
    // mirroring real instant-win lottery cards.
    const base = ctx.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, '#bcbecb');
    base.addColorStop(0.4, '#a4a6b3');
    base.addColorStop(1, '#7e8090');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    // Diagonal sheen — the "shine" sweep
    const sheen = ctx.createLinearGradient(0, 0, w, h);
    sheen.addColorStop(0, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.4, 'rgba(255,255,255,0.18)');
    sheen.addColorStop(0.55, 'rgba(255,255,255,0.4)');
    sheen.addColorStop(0.7, 'rgba(255,255,255,0.18)');
    sheen.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, w, h);

    // Speckle noise — adds the "matte foil" texture that reads as scratchable
    ctx.globalAlpha = 0.18;
    for (let i = 0; i < (w * h) / 800; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      ctx.fillStyle = Math.random() > 0.5 ? '#ffffff' : '#3a3c4a';
      ctx.fillRect(x, y, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Edge vignette so corners read as foil, not flat panel
    const vignette = ctx.createRadialGradient(
      w / 2,
      h / 2,
      Math.min(w, h) * 0.25,
      w / 2,
      h / 2,
      Math.max(w, h) * 0.7,
    );
    vignette.addColorStop(0, 'rgba(0,0,0,0)');
    vignette.addColorStop(1, 'rgba(0,0,0,0.4)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    // "SCRATCH" stamp on the silver layer — slightly sunken
    ctx.fillStyle = 'rgba(60, 60, 78, 0.55)';
    ctx.font = `800 ${Math.round(Math.min(w, h) * 0.13)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH', w / 2, h / 2 - Math.min(w, h) * 0.05);
    ctx.font = `700 ${Math.round(Math.min(w, h) * 0.06)}px system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(60, 60, 78, 0.55)';
    ctx.fillText('★  ★  ★', w / 2, h / 2 + Math.min(w, h) * 0.07);
  }, []);

  // Resize / DPR handling
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) return;

    const resize = (): void => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;
      sizeRef.current = { w: rect.width, h: rect.height };
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');

      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintFoil();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    return (): void => ro.disconnect();
  }, [paintFoil]);

  // Repaint foil whenever the active ticket changes (id-keyed remount also helps)
  useEffect(() => {
    revealedRef.current = false;
    setProgress(0);
    setShowHint(true);
    paintFoil();
  }, [ticketId, paintFoil]);

  const sampleCoverage = useCallback((): number => {
    const canvas = canvasRef.current;

    if (!canvas) return 0;
    const ctx = canvas.getContext('2d');

    if (!ctx) return 0;
    const { w, h } = sizeRef.current;
    const dpr = dprRef.current;
    const stepX = SAMPLE_STEP;
    const stepY = SAMPLE_STEP;
    let total = 0;
    let cleared = 0;
    // sample alpha on a coarse grid
    for (let y = stepY / 2; y < h; y += stepY) {
      for (let x = stepX / 2; x < w; x += stepX) {
        try {
          const data = ctx.getImageData(
            Math.floor(x * dpr),
            Math.floor(y * dpr),
            1,
            1,
          ).data;
          total += 1;

          if (data[3] < 32) cleared += 1;
        } catch {
          return 0;
        }
      }
    }

    if (total === 0) return 0;

    return cleared / total;
  }, []);

  const eraseAt = useCallback(
    (x: number, y: number): void => {
      const canvas = canvasRef.current;

      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      const last = lastPointRef.current;

      if (last) {
        ctx.lineWidth = radius * 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      lastPointRef.current = { x, y };
    },
    [radius],
  );

  const animatedFullReveal = useCallback((): void => {
    const canvas = canvasRef.current;

    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;
    const { w, h } = sizeRef.current;

    // Quick wipe: draw an expanding clear rect — feels like the foil
    // "evaporates" rather than disappearing instantly.
    let t = 0;
    const tick = (): void => {
      t += 0.12;
      const e = Math.min(1, t);
      ctx.globalCompositeOperation = 'destination-out';
      const cx = w / 2;
      const cy = h / 2;
      const r = Math.hypot(w, h) * e * 0.6;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#000';
      ctx.fill();

      if (e < 1) {
        requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    };
    requestAnimationFrame(tick);
  }, []);

  const triggerReveal = useCallback((): void => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setProgress(1);
    onProgress?.(1);
    animatedFullReveal();
    onReveal?.();

    if ('vibrate' in navigator) {
      try {
        navigator.vibrate?.([20, 40, 80]);
      } catch {
        /* haptics not supported */
      }
    }
  }, [animatedFullReveal, onProgress, onReveal]);

  const updateProgress = useCallback((): void => {
    const cov = sampleCoverage();
    setProgress(cov);
    onProgress?.(cov);

    if (cov >= threshold) triggerReveal();
  }, [onProgress, sampleCoverage, threshold, triggerReveal]);

  const getPoint = (
    e: ReactPointerEvent<HTMLCanvasElement>,
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current;

    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleDown = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (disabled || revealedRef.current) return;
    const canvas = canvasRef.current;

    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    lastPointRef.current = null;
    setShowHint(false);
    const p = getPoint(e);

    if (p) eraseAt(p.x, p.y);

    if ('vibrate' in navigator) {
      try {
        navigator.vibrate?.(8);
      } catch {
        /* ignore */
      }
    }
  };

  const handleMove = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (!isDrawingRef.current || disabled || revealedRef.current) return;
    const p = getPoint(e);

    if (!p) return;
    eraseAt(p.x, p.y);
  };

  const handleUp = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (disabled || revealedRef.current) return;
    const canvas = canvasRef.current;

    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    isDrawingRef.current = false;
    lastPointRef.current = null;
    updateProgress();
  };

  return (
    <div ref={containerRef} className={cn(css.wrap, className)}>
      <canvas
        ref={canvasRef}
        className={css.canvas}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onPointerLeave={handleUp}
        style={{ touchAction: 'none' }}
      />
      {showHint && progress < 0.05 && !disabled && (
        <div className={css.hint}>
          <div className={css.hintFinger}>
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden>
              <path
                d="M14 4c-1.1 0-2 .9-2 2v9.5L9.5 13c-.8-.8-2-.8-2.8 0s-.8 2 0 2.8L13 21.6V26a2 2 0 0 0 2 2h6a4 4 0 0 0 4-4V14c0-1.1-.9-2-2-2s-2 .9-2 2v6h-1V8c0-1.1-.9-2-2-2s-2 .9-2 2v10h-1V6c0-1.1-.9-2-2-2z"
                fill="#fff"
              />
            </svg>
          </div>
          <span className={css.hintText}>{t('Swipe to scratch')}</span>
        </div>
      )}
    </div>
  );
};

export { ScratchSurface };
